import { adminSupabase } from './supabaseService';
import { userService } from './userService';

/*
REQUIRED SQL MIGRATION:
Run this SQL in your Supabase SQL editor to make rule_id nullable in sniper_trade_history:

ALTER TABLE sniper_trade_history 
ALTER COLUMN rule_id DROP NOT NULL;

This allows us to set rule_id to null when deleting rules, preserving trade history.
*/

export interface SniperRule {
    id: string;
    user_id: string;
    name: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    last_triggered_at?: string;
    total_triggers: number;
    total_executed_trades: number;
    conditions: SniperCondition[];
    sol_amount_per_snipe?: number;
    max_tokens_to_snipe?: number;
    trading_wallet_addresses?: string[];
    total_sol_budget?: number;
    slippage_percent?: number;
    configuration_metadata?: any;
}

export interface SniperCondition {
    id?: string;
    rule_id?: string;
    field: 'marketCap' | 'liquidity' | 'holders' | 'buys' | 'sells' | 'curvePercentage' | 'txAmount' | 'txVolume' | 'name' | 'symbol';
    operator: '>' | '<' | '>=' | '<=' | '=' | 'increase%' | 'decrease%';
    value: number;
    timeframe?: '1m' | '5m' | '15m' | '1h';
    created_at?: string;
}

export interface SniperTradeHistory {
    id: string;
    rule_id: string | null;
    user_id: string;
    token_address: string;
    token_name?: string;
    token_symbol?: string;
    trading_wallet_address: string;
    amount_sol: number;
    slippage_percent: number;
    transaction_signature?: string;
    status: 'pending' | 'success' | 'failed';
    error_message?: string;
    rule_name?: string;
    triggered_conditions?: any[];
    created_at: string;
    executed_at?: string;
}

export const sniperRulesService = {
    /**
     * Get all sniper rules for a user
     */
    async getUserSniperRules(walletAddress: string): Promise<SniperRule[]> {
        try {
            // First get the user ID
            const user = await userService.getUserByWallet(walletAddress);
            if (!user) {
                console.warn('User not found for wallet address:', walletAddress);
                return [];
            }

            // Get rules with their conditions
            const { data: rules, error: rulesError } = await adminSupabase
                .from('sniper_rules')
                .select(`
          *,
          sniper_conditions (*)
        `)
                .eq('user_id', user.id)
                .order('created_at', { ascending: false });

            if (rulesError) {
                console.error('Error fetching sniper rules:', rulesError);
                return [];
            }

            // Transform the data to match our interface
            return (rules || []).map(rule => ({
                ...rule,
                conditions: rule.sniper_conditions || []
            }));
        } catch (error) {
            console.error('Error in getUserSniperRules:', error);
            return [];
        }
    },

    /**
     * Create a new sniper rule
     */
    async createSniperRule(
        walletAddress: string,
        name: string,
        conditions: Omit<SniperCondition, 'id' | 'rule_id' | 'created_at'>[],
        isActive: boolean = false, // Default to inactive/stopped state
        config?: {
            solAmountPerSnipe?: number;
            maxTokensToSnipe?: number;
            slippagePercent?: number;
            tradingWalletAddresses?: string[];
            totalSolBudget?: number;
            configurationMetadata?: any;
        }
    ): Promise<SniperRule | null> {
        try {
            // First get the user ID
            const user = await userService.getUserByWallet(walletAddress);
            if (!user) {
                console.error('User not found for wallet address:', walletAddress);
                return null;
            }

            // Create the rule
            const { data: rule, error: ruleError } = await adminSupabase
                .from('sniper_rules')
                .insert({
                    user_id: user.id,
                    name,
                    is_active: isActive,
                    sol_amount_per_snipe: config?.solAmountPerSnipe,
                    max_tokens_to_snipe: config?.maxTokensToSnipe,
                    slippage_percent: config?.slippagePercent,
                    trading_wallet_addresses: config?.tradingWalletAddresses,
                    total_sol_budget: config?.totalSolBudget,
                    configuration_metadata: config?.configurationMetadata
                })
                .select()
                .single();

            if (ruleError) {
                console.error('Error creating sniper rule:', ruleError);
                return null;
            }

            // Create the conditions
            if (conditions.length > 0) {
                const conditionsToInsert = conditions.map(condition => ({
                    rule_id: rule.id,
                    ...condition
                }));

                const { data: createdConditions, error: conditionsError } = await adminSupabase
                    .from('sniper_conditions')
                    .insert(conditionsToInsert)
                    .select();

                if (conditionsError) {
                    console.error('Error creating sniper conditions:', conditionsError);
                    // Clean up the rule if conditions failed
                    await adminSupabase.from('sniper_rules').delete().eq('id', rule.id);
                    return null;
                }

                rule.conditions = createdConditions || [];
            } else {
                rule.conditions = [];
            }

            return rule;
        } catch (error) {
            console.error('Error in createSniperRule:', error);
            return null;
        }
    },

    /**
     * Update an existing sniper rule
     */
    async updateSniperRule(
        walletAddress: string,
        ruleId: string,
        updates: {
            name?: string;
            is_active?: boolean;
            conditions?: Omit<SniperCondition, 'id' | 'rule_id' | 'created_at'>[];
            solAmountPerSnipe?: number;
            maxTokensToSnipe?: number;
            slippagePercent?: number;
            tradingWalletAddresses?: string[];
            totalSolBudget?: number;
            configurationMetadata?: any;
        }
    ): Promise<SniperRule | null> {
        try {
            // First get the user ID
            const user = await userService.getUserByWallet(walletAddress);
            if (!user) {
                console.error('User not found for wallet address:', walletAddress);
                return null;
            }

            // Update the rule
            const ruleUpdates: any = {};
            if (updates.name !== undefined) ruleUpdates.name = updates.name;
            if (updates.is_active !== undefined) ruleUpdates.is_active = updates.is_active;
            if (updates.solAmountPerSnipe !== undefined) ruleUpdates.sol_amount_per_snipe = updates.solAmountPerSnipe;
            if (updates.maxTokensToSnipe !== undefined) ruleUpdates.max_tokens_to_snipe = updates.maxTokensToSnipe;
            if (updates.slippagePercent !== undefined) ruleUpdates.slippage_percent = updates.slippagePercent;
            if (updates.tradingWalletAddresses !== undefined) ruleUpdates.trading_wallet_addresses = updates.tradingWalletAddresses;
            if (updates.totalSolBudget !== undefined) ruleUpdates.total_sol_budget = updates.totalSolBudget;
            if (updates.configurationMetadata !== undefined) ruleUpdates.configuration_metadata = updates.configurationMetadata;

            const { data: rule, error: ruleError } = await adminSupabase
                .from('sniper_rules')
                .update(ruleUpdates)
                .eq('id', ruleId)
                .eq('user_id', user.id)
                .select()
                .single();

            if (ruleError) {
                console.error('Error updating sniper rule:', ruleError);
                return null;
            }

            // If conditions are provided, update them
            if (updates.conditions !== undefined) {
                // Delete existing conditions
                await adminSupabase
                    .from('sniper_conditions')
                    .delete()
                    .eq('rule_id', ruleId);

                // Insert new conditions
                if (updates.conditions.length > 0) {
                    const conditionsToInsert = updates.conditions.map(condition => ({
                        rule_id: ruleId,
                        ...condition
                    }));

                    const { data: createdConditions, error: conditionsError } = await adminSupabase
                        .from('sniper_conditions')
                        .insert(conditionsToInsert)
                        .select();

                    if (conditionsError) {
                        console.error('Error updating sniper conditions:', conditionsError);
                        return null;
                    }

                    rule.conditions = createdConditions || [];
                } else {
                    rule.conditions = [];
                }
            } else {
                // Fetch existing conditions
                const { data: conditions } = await adminSupabase
                    .from('sniper_conditions')
                    .select('*')
                    .eq('rule_id', ruleId);

                rule.conditions = conditions || [];
            }

            return rule;
        } catch (error) {
            console.error('Error in updateSniperRule:', error);
            return null;
        }
    },

    /**
     * Delete a sniper rule
     */
    async deleteSniperRule(walletAddress: string, ruleId: string): Promise<boolean> {
        try {
            // First get the user ID
            const user = await userService.getUserByWallet(walletAddress);
            if (!user) {
                console.error('User not found for wallet address:', walletAddress);
                return false;
            }

            // FIXED: Set rule_id to null to preserve trade history (requires rule_id to be nullable)
            console.log(`🔄 Checking for trade history records for rule ${ruleId}...`);

            // First check if there are any trade history records for this rule
            const { data: existingTrades, error: checkError } = await adminSupabase
                .from('sniper_trade_history')
                .select('id')
                .eq('rule_id', ruleId)
                .eq('user_id', user.id);

            if (checkError) {
                console.error('Error checking existing trade history:', checkError);
                return false;
            }

            console.log(`📊 Found ${existingTrades?.length || 0} trade history records for rule ${ruleId}`);

            // Set rule_id to null to preserve trade history while allowing rule deletion
            if (existingTrades && existingTrades.length > 0) {
                console.log(`🔄 Setting rule_id to null for ${existingTrades.length} trade history records to preserve them...`);
                const { error: updateError } = await adminSupabase
                    .from('sniper_trade_history')
                    .update({ rule_id: null })
                    .eq('rule_id', ruleId)
                    .eq('user_id', user.id);

                if (updateError) {
                    console.error('Error updating trade history before rule deletion:', updateError);
                    console.error('Update error details:', JSON.stringify(updateError, null, 2));
                    console.error('⚠️ Make sure you have run the SQL migration to make rule_id nullable!');
                    return false;
                }
                console.log(`✅ Successfully preserved ${existingTrades.length} trade history records`);
            } else {
                console.log(`ℹ️ No trade history records found for rule ${ruleId}, proceeding with rule deletion`);
            }

            // Now delete the rule (conditions will be deleted automatically due to CASCADE)
            console.log(`🗑️ Deleting sniper rule ${ruleId}...`);
            const { error } = await adminSupabase
                .from('sniper_rules')
                .delete()
                .eq('id', ruleId)
                .eq('user_id', user.id);

            if (error) {
                console.error('Error deleting sniper rule:', error);
                console.error('Rule deletion error details:', JSON.stringify(error, null, 2));
                return false;
            }

            console.log(`✅ Successfully deleted rule ${ruleId}`);
            return true;
        } catch (error) {
            console.error('Error in deleteSniperRule:', error);
            return false;
        }
    },

    /**
     * Toggle rule active status
     */
    async toggleRuleActive(walletAddress: string, ruleId: string): Promise<boolean> {
        try {
            // First get the user ID
            const user = await userService.getUserByWallet(walletAddress);
            if (!user) {
                console.error('User not found for wallet address:', walletAddress);
                return false;
            }

            // Get current status
            const { data: rule } = await adminSupabase
                .from('sniper_rules')
                .select('is_active')
                .eq('id', ruleId)
                .eq('user_id', user.id)
                .single();

            if (!rule) {
                console.error('Rule not found');
                return false;
            }

            // Toggle the status
            const { error } = await adminSupabase
                .from('sniper_rules')
                .update({ is_active: !rule.is_active })
                .eq('id', ruleId)
                .eq('user_id', user.id);

            if (error) {
                console.error('Error toggling rule status:', error);
                return false;
            }

            return true;
        } catch (error) {
            console.error('Error in toggleRuleActive:', error);
            return false;
        }
    },

    /**
     * Record a sniper trade execution
     */
    async recordSniperTrade(
        walletAddress: string,
        ruleId: string,
        tokenAddress: string,
        tokenName: string,
        tokenSymbol: string,
        tradingWalletAddress: string,
        amountSol: number,
        slippagePercent: number,
        transactionSignature?: string,
        status: 'pending' | 'success' | 'failed' = 'pending',
        errorMessage?: string,
        triggeredConditions?: any[]
    ): Promise<SniperTradeHistory | null> {
        try {
            // First get the user ID
            const user = await userService.getUserByWallet(walletAddress);
            if (!user) {
                console.error('User not found for wallet address:', walletAddress);
                return null;
            }

            const { data: trade, error } = await adminSupabase
                .from('sniper_trade_history')
                .insert({
                    rule_id: ruleId,
                    user_id: user.id,
                    token_address: tokenAddress,
                    token_name: tokenName,
                    token_symbol: tokenSymbol,
                    trading_wallet_address: tradingWalletAddress,
                    amount_sol: amountSol,
                    slippage_percent: slippagePercent,
                    transaction_signature: transactionSignature,
                    status,
                    error_message: errorMessage,
                    triggered_conditions: triggeredConditions,
                    executed_at: status !== 'pending' ? new Date().toISOString() : undefined
                })
                .select()
                .single();

            if (error) {
                console.error('Error recording sniper trade:', error);
                return null;
            }

            // Update rule statistics
            if (status === 'success') {
                // Get current values
                const { data: currentExecutedRule } = await adminSupabase
                    .from('sniper_rules')
                    .select('total_executed_trades')
                    .eq('id', ruleId)
                    .single();

                await adminSupabase
                    .from('sniper_rules')
                    .update({
                        total_executed_trades: (currentExecutedRule?.total_executed_trades || 0) + 1,
                        last_triggered_at: new Date().toISOString()
                    })
                    .eq('id', ruleId);
            }

            // Get current trigger count and increment
            const { data: currentTriggeredRule } = await adminSupabase
                .from('sniper_rules')
                .select('total_triggers')
                .eq('id', ruleId)
                .single();

            await adminSupabase
                .from('sniper_rules')
                .update({
                    total_triggers: (currentTriggeredRule?.total_triggers || 0) + 1
                })
                .eq('id', ruleId);

            return trade;
        } catch (error) {
            console.error('Error in recordSniperTrade:', error);
            return null;
        }
    },

    /**
     * Get trade history for a user
     */
    async getUserTradeHistory(walletAddress: string, limit: number = 50): Promise<SniperTradeHistory[]> {
        try {
            // First get the user ID
            const user = await userService.getUserByWallet(walletAddress);
            if (!user) {
                console.error('User not found for wallet address:', walletAddress);
                return [];
            }

            // FIXED: Get trade history without requiring rule to exist (rule_id can be null)
            const { data: trades, error } = await adminSupabase
                .from('sniper_trade_history')
                .select('*')
                .eq('user_id', user.id)
                .order('created_at', { ascending: false })
                .limit(limit);

            if (error) {
                console.error('Error fetching trade history:', error);
                return [];
            }

            // FIXED: Manually fetch rule names for trades that still have rule_id
            const tradesWithRuleNames = await Promise.all(
                (trades || []).map(async (trade) => {
                    if (trade.rule_id) {
                        // Try to get the rule name
                        const { data: rule } = await adminSupabase
                            .from('sniper_rules')
                            .select('name')
                            .eq('id', trade.rule_id)
                            .single();

                        return {
                            ...trade,
                            rule_name: rule?.name || 'Unknown Bot'
                        };
                    } else {
                        // Rule was deleted, show as "Deleted Bot"
                        return {
                            ...trade,
                            rule_name: 'Deleted Bot'
                        };
                    }
                })
            );

            return tradesWithRuleNames;
        } catch (error) {
            console.error('Error in getUserTradeHistory:', error);
            return [];
        }
    }
};

// Field display names for UI
export const SNIPER_FIELD_LABELS = {
    marketCap: 'Market Cap (USD)',
    liquidity: 'Liquidity (USD)',
    holders: 'Holders Count',
    buys: 'Buy Count',
    sells: 'Sell Count',
    curvePercentage: 'Curve %',
    txAmount: 'Tokens Bought',
    txVolume: 'Transaction Volume (USD)',
    name: 'Token Name',
    symbol: 'Token Symbol'
};

// Operator display names for UI
export const SNIPER_OPERATOR_LABELS = {
    '>': 'Greater than',
    '<': 'Less than',
    '>=': 'Greater than or equal',
    '<=': 'Less than or equal',
    '=': 'Equal to',
    'increase%': 'Increase by %',
    'decrease%': 'Decrease by %'
};

// Timeframe display names for UI
export const SNIPER_TIMEFRAME_LABELS = {
    '1m': '1 minute',
    '5m': '5 minutes',
    '15m': '15 minutes',
    '1h': '1 hour'
}; 